import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
const EMPTY_RECT = Object.freeze({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
});
/**
 * Measures an element's bounding rect on attach, on resize, and synchronously
 * before paint whenever `measureKey` changes. Returns a callback ref, the frozen
 * rect, and whether a measurement has been taken.
 */
export function useSyncMeasure(measureKey) {
    const [rect, setRect] = useState(EMPTY_RECT);
    const elementRef = useRef(null);
    const observerRef = useRef(null);
    const measure = useCallback((element) => {
        const next = element.getBoundingClientRect();
        setRect((prev) => prev !== EMPTY_RECT &&
            prev.width === next.width &&
            prev.height === next.height &&
            prev.left === next.left &&
            prev.top === next.top
            ? prev
            : Object.freeze({
                x: next.x,
                y: next.y,
                width: next.width,
                height: next.height,
                top: next.top,
                left: next.left,
                bottom: next.bottom,
                right: next.right,
            }));
    }, []);
    const ref = useCallback((element) => {
        observerRef.current?.disconnect();
        observerRef.current = null;
        elementRef.current = element;
        if (!element) {
            setRect(EMPTY_RECT);
            return;
        }
        measure(element);
        if (typeof ResizeObserver === 'undefined')
            return;
        const observer = new ResizeObserver(() => measure(element));
        observer.observe(element);
        observerRef.current = observer;
    }, [measure]);
    useLayoutEffect(() => {
        const element = elementRef.current;
        if (!element)
            return;
        // Synchronous re-read before paint when parent layout inputs change.
        measure(element);
    }, [measure, measureKey]);
    useEffect(() => {
        return () => observerRef.current?.disconnect();
    }, []);
    return [ref, rect, rect !== EMPTY_RECT];
}
//# sourceMappingURL=useSyncMeasure.js.map